// =================================================================================================
// API timestamp display
//
// Display rule: a value is converted to the browser's local timezone only when it is an
// unambiguous instant — an ISO string carrying timezone info (Z or ±HH:MM/±HHMM), an epoch
// number, or a Date. A naive datetime string (no offset) is reformatted for readability but
// its wall-clock fields are never shifted, since its timezone is unknown.

// Timezone-aware suffix: Z, +09:00, -0500. A date-only tail like "-02-25" cannot match
// (requires sign + 2 digits + optional colon + 2 digits at the very end).
const TZ_OFFSET_RE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

// Naive ISO-ish: YYYY-MM-DD[T or space]HH:mm[:ss[.frac]] (frac may be ms or µs)
const NAIVE_DATETIME_RE =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?$/;

const FULL_OPTS = {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
};
const SHORT_OPTS = {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
};

// Returns a Date for FORMATTING, or null (empty input) / undefined (unrecognized input).
// For naive strings the Date is built from the literal fields — a formatting vehicle only,
// never interpreted as an instant.
function toDisplayDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value); // epoch ms
  if (typeof value !== "string") return undefined;
  const s = value.trim();
  if (s === "") return null;
  if (TZ_OFFSET_RE.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }
  const m = NAIVE_DATETIME_RE.exec(s);
  if (m) {
    return new Date(
      +m[1],
      +m[2] - 1,
      +m[3],
      +m[4],
      +m[5],
      +(m[6] || 0),
      +String(m[7] || "0")
        .slice(0, 3)
        .padEnd(3, "0")
    );
  }
  return undefined;
}

// Full display with seconds. Empty input -> fallback; unrecognized input -> returned verbatim.
export function formatDateTime(value, fallback = "") {
  const d = toDisplayDate(value);
  if (d === null) return fallback;
  if (d === undefined) return String(value);
  return d.toLocaleString(undefined, FULL_OPTS);
}

// Compact display without seconds (table density).
export function formatShortDateTime(value, fallback = "") {
  const d = toDisplayDate(value);
  if (d === null) return fallback;
  if (d === undefined) return String(value);
  return d.toLocaleString(undefined, SHORT_OPTS);
}

// Epoch nanoseconds (BigInt/number/string) -> full local display.
export function formatEpochNs(nsValue, fallback = "—") {
  if (nsValue == null) return fallback;
  const nsStr = nsValue.toString();
  if (!/^\d+$/.test(nsStr)) return fallback;
  const ms = nsStr.length > 6 ? parseInt(nsStr.slice(0, nsStr.length - 6), 10) : 0;
  return formatDateTime(ms, fallback);
}

// For COMPARISONS (not display): returns a real Date instant, or null.
// Naive strings are assumed UTC — matches raasdata-service storage semantics.
export function parseApiInstant(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  const s = String(value).trim();
  if (!TZ_OFFSET_RE.test(s) && NAIVE_DATETIME_RE.test(s)) {
    return new Date(s.replace(" ", "T") + "Z");
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

// UTC day key "YYYY-MM-DD" for joining server-side UTC aggregations.
export function utcDayKey(epochMs) {
  return new Date(epochMs).toISOString().slice(0, 10);
}

// =================================================================================================
export function iterateYears(startDate, endDate, callback) {
  let currentDate = new Date(startDate.getFullYear(), 0, 1);
  const end = new Date(endDate.getFullYear(), 0, 1);

  while (currentDate <= end) {
    callback(new Date(currentDate)); // Pass a copy to avoid mutation
    currentDate.setFullYear(currentDate.getFullYear() + 1);
  }
}

// // Example usage:
// const start = new Date(2020, 0, 1); // January 1, 2020
// const end = new Date(2025, 0, 1);   // January 1, 2025

// iterateYears(start, end, (date) => {
//     console.log(date.toISOString().slice(0, 4)); // Logs YYYY
// });

// =================================================================================================
export function iterateMonths(startDate, endDate, callback) {
  let currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

  while (currentDate <= end) {
    callback(new Date(currentDate)); // Pass a copy to avoid mutation
    currentDate.setMonth(currentDate.getMonth() + 1);
  }
}

// // Example usage:
// // const start = new Date(2023, 0, 1); // January 1, 2023
// const end = new Date(2023, 5, 1);   // June 1, 2023

// iterateMonths(start, end, (date) => {
//     console.log(date.toISOString().slice(0, 7)); // Logs YYYY-MM
// }

// =================================================================================================
export function iterateWeeks(startDate, endDate, callback) {
  let currentDate = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );
  const end = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate()
  );

  while (currentDate <= end) {
    callback(new Date(currentDate)); // Pass a copy to avoid mutation
    currentDate.setDate(currentDate.getDate() + 7);
  }
}

// // Example usage:
// const start = new Date(2023, 0, 1); // January 1, 2023
// const end = new Date(2023, 1, 1);   // February 1, 2023

// iterateWeeks(start, end, (date) => {
//     console.log(date.toISOString().slice(0, 10)); // Logs YYYY-MM-DD
// });

// iterateDays(start, end, (date) => {
//     console.log(date.toISOString().slice(0, 10), date.toLocaleString('en-US', { weekday: 'long' })); // Logs YYYY-MM-DD and week name
// });

// =================================================================================================
export function iterateDays(startDate, endDate, callback) {
  let currentDate = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );
  const end = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate()
  );

  while (currentDate <= end) {
    callback(new Date(currentDate)); // Pass a copy to avoid mutation
    currentDate.setDate(currentDate.getDate() + 1);
  }
}

// // Example usage:
// const start = new Date(2023, 0, 1); // January 1, 2023
// const end = new Date(2023, 0, 10);   // January 10, 2023

// iterateDays(start, end, (date) => {
//     console.log(date.toISOString().slice(0, 10)); // Logs YYYY-MM-DD
// });

// =================================================================================================
export const getTimeBlockKey = (time, period = "MONTH") => {
  if (time == null) {
    return "N/A";
  }
  if (typeof time === "string") {
    time = new Date(time);
  }

  if (!(time instanceof Date)) {
    return time + "";
  }

  switch (period.toLowerCase()) {
    case "year":
      return "" + time.getFullYear();
    case "month":
      return time.getFullYear() + "-" + time.getMonth();
    case "week":
      //get the date of the last monday from the "time" date
      const weekDay = time.getDay();
      const monday = new Date(time);
      monday.setDate(time.getDate() - weekDay);
      return (
        monday.getFullYear() + "-" + monday.getMonth() + "-w" + monday.getDate()
      );
    case "day":
      return time.getFullYear() + "-" + time.getMonth() + "-" + time.getDate();
    default:
      return null;
  }
};

// =================================================================================================
export const getTimeBlockKeysToIndex = (
  minDate,
  maxDate,
  timePeriod = "month"
) => {
  const timeBlocksMap = {};
  let count = 0;
  switch (timePeriod) {
    case "year":
      iterateYears(minDate, maxDate, (date) => {
        timeBlocksMap[getTimeBlockKey(date, timePeriod)] = count++;
      });
      break;
    case "month":
      iterateMonths(minDate, maxDate, (date) => {
        timeBlocksMap[getTimeBlockKey(date, timePeriod)] = count++;
      });
      break;
    case "week":
      iterateWeeks(minDate, maxDate, (date) => {
        timeBlocksMap[getTimeBlockKey(date, timePeriod)] = count++;
      });
      break;
    case "day":
      iterateDays(minDate, maxDate, (date) => {
        timeBlocksMap[getTimeBlockKey(date, timePeriod)] = count++;
      });
      break;
    default:
      break;
  }
  return timeBlocksMap;
};
